import type { LabResultRecord } from '../types/labs';
import type { BiomarkerMeta } from '../data/biomarkerCatalogue';
import { resolveBiomarker } from '../data/biomarkerCatalogue';
import { computeBiomarkerStatus, type BiomarkerStatusResult } from './biomarkerStatus';

/**
 * Build a biomarker's history from the already-loaded lab records — no extra
 * fetch. Points are in canonical units (via the status engine, which handles
 * unit conversion) and sorted oldest → newest, so the trend line reads
 * left-to-right in time. Non-numeric / unit-mismatched readings are dropped.
 */

export interface BiomarkerHistoryPoint {
  observedAt: string | null;
  timestamp: number;
  value: number;
  status: BiomarkerStatusResult;
}

export function buildBiomarkerHistory(
  records: LabResultRecord[],
  meta: BiomarkerMeta,
  sex?: string | null,
): BiomarkerHistoryPoint[] {
  const points: BiomarkerHistoryPoint[] = [];
  for (const record of records) {
    const resolved = resolveBiomarker({ biomarkerKey: record.biomarkerKey, biomarker: record.biomarker });
    if (!resolved || resolved.key !== meta.key) continue;
    const status = computeBiomarkerStatus(record, meta, sex);
    if (!status.hasBar || status.canonicalValue == null) continue;
    const parsed = record.observedAt ? new Date(record.observedAt).getTime() : NaN;
    points.push({
      observedAt: record.observedAt ?? null,
      timestamp: Number.isNaN(parsed) ? 0 : parsed,
      value: status.canonicalValue,
      status,
    });
  }
  return points.sort((left, right) => left.timestamp - right.timestamp);
}
